import { useTranslation } from "react-i18next";
import { useQueryClient } from "react-query";

import { useJsonApi } from "@/modules/api/hooks/useJsonApi";
import { useMutation } from "@/modules/api/hooks/useMutation";
import { queryKeys } from "@/modules/api/queryKeys";
import { apiErrorValidate } from "@/modules/api/utils/apiErrorValidate";
import { notify } from "@/modules/notify/notify";
import { undoSubtaskCompletionMutation } from "@/modules/tasks/api/mutations/undoSubtaskCompletionMutation";

const translationNamespace = "apiMessages/useUndoSubtaskCompletionMutation";

export const useUndoSubtaskCompletionMutation = (onSuccess?: () => void) => {
  const jsonApi = useJsonApi();
  const queryClient = useQueryClient();

  const { t } = useTranslation(translationNamespace);

  return useMutation([undoSubtaskCompletionMutation.name], {
    mutationFn: undoSubtaskCompletionMutation(jsonApi),
    onSuccess: async () => {
      await queryClient.invalidateQueries(queryKeys.tasks.getTasksList);
      notify.alert.success(t("The action has been performed successfully"));
      onSuccess && onSuccess();
    },
    onError: (error) => {
      apiErrorValidate(error, {
        401: {
          UNAUTHORIZED: t(
            "You are not allowed to view the subtask. Please sign in again"
          ),
        },
        403: {
          FORBIDDEN: t("You don't have an access to view the subtask"),
        },
        404: {
          SUBTASK_NOT_FOUND: t(
            "The subtask cannot be found. Please check if you have required permissions"
          ),
        },
        422: {
          SUBTASK_NOT_COMPLETED: t(
            "The subtask hasn't been completed yet. Please try again later"
          ),
        },
      });
    },
    retry: false,
  });
};
